<div class="ai-tab-panel" #aiTabPanel>
  <div class="ai-chat-header">
    <mat-chip class="ai-chat-beta-chip">
      <span translate="SETUP_GUIDE.TABS.BETA"></span>
    </mat-chip>
    <span class="ai-chat-header-title" [matTooltip]="currentSessionTitle">
      {{ currentSessionTitle || ('SETUP_GUIDE.AI_CHAT.NO_SESSION_TITLE' | translate) }}
    </span>
    <div class="ai-chat-header-actions">
      <button
        type="button"
        mat-icon-button
        class="ai-chat-history-button"
        [matMenuTriggerFor]="historyMenu"
        [matTooltip]="'SETUP_GUIDE.AI_CHAT.HISTORY' | translate"
        [disabled]="!filteredAiChatSessions.length"
      >
        <mat-icon>history</mat-icon>
      </button>
      <mat-menu #historyMenu="matMenu" class="ai-history-menu">
        @for (session of filteredAiChatSessions; track session.id) {
          <button mat-menu-item (click)="onLoadSessionFromMenu(session.id)">
            <span>{{ session.title }}</span>
          </button>
        } @empty {
          <button mat-menu-item disabled>
            <span>{{ 'SETUP_GUIDE.AI_CHAT.LOAD_SESSION' | translate }}</span>
          </button>
        }
      </mat-menu>
      <button
        type="button"
        mat-icon-button
        class="ai-chat-new-button"
        (click)="startNewAiChatSession()"
        [matTooltip]="'SETUP_GUIDE.AI_CHAT.NEW_CHAT' | translate"
      >
        <mat-icon>add_comment</mat-icon>
      </button>
    </div>
  </div>

  <div class="setup-with-ai-conversation-shell">
    @if (aiChatMessages.length) {
      <div class="ai-conversation-timeline" #aiConversationTimeline>
        @for (message of aiChatMessages; track message.id) {
          <div class="ai-chat-message-row" [class.ai-chat-message-row-right]="message.side === 'right'">
            <div class="ai-chat-message-card" [class.ai-chat-message-card-user]="message.side === 'right'">
              <div class="ai-chat-message-header">
                <mat-icon>{{ getMessageIcon(message.type) }}</mat-icon>
                <span>{{ 'SETUP_GUIDE.AI_CHAT.MESSAGE_TYPE.' + message.type.toUpperCase() | translate }}</span>
                @if (
                  message.type === SetupGuideAiMessageTypeEnum.User &&
                  editingMessageId !== message.id &&
                  !hasGeneratedImage
                ) {
                  <button type="button" class="ai-chat-edit-button" (click)="onEditUserMessage(message)">
                    <mat-icon>edit</mat-icon>
                  </button>
                }
              </div>

              <div class="ai-chat-message-content">
                @if (editingMessageId === message.id) {
                  <textarea class="ai-chat-edit-textarea" [formControl]="editContentControl" rows="2"></textarea>
                  <div class="ai-chat-edit-actions">
                    <button type="button" mat-stroked-button class="ai-chat-edit-cancel" (click)="onCancelEdit()">
                      <span translate="SETUP_GUIDE.AI_CHAT.CANCEL"></span>
                    </button>
                    <button
                      type="button"
                      mat-flat-button
                      color="primary"
                      class="ai-chat-edit-save"
                      (click)="onSaveEditedMessage(message)"
                    >
                      <span translate="SETUP_GUIDE.AI_CHAT.SAVE_AND_REGENERATE"></span>
                    </button>
                  </div>
                } @else {
                  @if (message.type === SetupGuideAiMessageTypeEnum.Assistant) {
                    <div
                      class="ai-chat-markdown-content"
                      [innerHTML]="renderMarkdown(message.content)"
                      (click)="onMarkdownLinkClick($event)"
                    ></div>
                  } @else if (
                    message.type === SetupGuideAiMessageTypeEnum.ToolCall ||
                    message.type === SetupGuideAiMessageTypeEnum.ToolResult
                  ) {
                    {{ message.content | translate }}
                  } @else {
                    {{ message.content }}
                  }
                  @if (message.showLoadingDots) {
                    <span class="ai-chat-loading-dots" aria-hidden="true"></span>
                  }
                }
              </div>

              @if (message.type === SetupGuideAiMessageTypeEnum.ToolResult) {
                @switch (getToolDisplayType(message)) {
                  @case ('colors') {
                    <div class="ai-tool-colors-preview-list">
                      @for (entry of getBrandingColorPreviewEntries(message); track entry.key) {
                        <div class="ai-tool-color-preview-item">
                          <div class="ai-tool-color-preview-swatch" [style.background-color]="entry.value"></div>
                          <div class="ai-tool-color-preview-details">
                            <div class="ai-tool-color-preview-label">{{ entry.label }}</div>
                            <div class="ai-tool-color-preview-value">{{ entry.value }}</div>
                          </div>
                        </div>
                      }
                    </div>
                  }
                  @case ('css') {
                    <div class="ai-tool-preview-card">
                      <pre class="ai-tool-css-preview">{{ getCustomCssPreview(message) }}</pre>
                    </div>
                  }
                  @case ('articles') {
                    <div class="ai-tool-articles-preview-list">
                      @for (article of getArticlePreviewEntries(message); track article.url) {
                        <a
                          class="ai-tool-article-preview-item"
                          [href]="article.url"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <div class="ai-tool-article-preview-title">{{ article.title }}</div>
                          <div class="ai-tool-article-preview-meta">{{ article.topic }}</div>
                        </a>
                      }
                    </div>
                  }
                  @case ('article-content') {
                    <div class="ai-tool-preview-card">
                      <div class="ai-tool-article-content-title">{{ getArticleContentTitle(message) }}</div>
                      <pre class="ai-tool-css-preview">{{ getArticleContentSnippet(message) }}</pre>
                      @if (getArticleContentUrl(message)) {
                        <a
                          class="ai-tool-article-read-more"
                          [href]="getArticleContentUrl(message)"
                          target="_blank"
                          rel="noopener noreferrer"
                          translate="SETUP_GUIDE.AI_CHAT.READ_MORE"
                        ></a>
                      }
                    </div>
                  }
                  @case ('image') {
                    <div class="ai-tool-image-preview">
                      <img [src]="getGeneratedImagePreview(message)" alt="Generated image preview" />
                    </div>
                  }
                  @default {
                    <div class="ai-tool-preview-card">
                      @for (line of getToolResultPreviewLines(message); track line) {
                        <div class="ai-tool-preview-line">{{ line }}</div>
                      }
                    </div>
                  }
                }

                @if (canApplyToolResult(message)) {
                  <button
                    type="button"
                    mat-flat-button
                    color="primary"
                    class="ai-tool-apply-button"
                    (click)="onApplyToolResult(message)"
                  >
                    <span translate="SETUP_GUIDE.AI_CHAT.APPLY"></span>
                  </button>
                }
              }
            </div>
          </div>
        }
      </div>
    } @else {
      <div class="ai-chat-empty-state">
        <mat-icon>smart_toy</mat-icon>
        <span translate="SETUP_GUIDE.AI_CHAT.EMPTY_STATE"></span>
      </div>
    }
    <div class="ai-chat-disclaimer">
      <span translate="SETUP_GUIDE.AI_CHAT.DISCLAIMER"></span>
      @if (isStaff) {
        <a class="ai-chat-disclaimer-link" [gbRouteNewTab]="'/help'">
          <span translate="SETUP_GUIDE.AI_CHAT.DISCLAIMER_SUPPORT_LINK"></span>
        </a>
      } @else {
        <a class="ai-chat-disclaimer-link" (click)="onContactSupportClick($event)">
          <span translate="SETUP_GUIDE.AI_CHAT.DISCLAIMER_SUPPORT_LINK"></span>
        </a>
      }
      <span translate="SETUP_GUIDE.AI_CHAT.DISCLAIMER_SUFFIX"></span>
    </div>
  </div>

  <div class="setup-with-ai-chat-shell">
    @if (!hasGeneratedImage && !(isAiBusy$ | async) && aiMode === SetupGuideAiModeEnum.Act) {
      <div class="ai-tool-chips">
        <span
          class="ai-tool-chips-label"
          [translate]="selectedToolConfig ? 'SETUP_GUIDE.AI_CHAT.SELECTED_TOOL' : 'SETUP_GUIDE.AI_CHAT.AVAILABLE_TOOLS'"
        ></span>
        <mat-chip-set aria-label="Available tools">
          @if (selectedToolConfig) {
            <mat-chip class="ai-tool-chip-selected" [removable]="true" (removed)="onClearSelectedTool()">
              {{ selectedToolConfig.labelKey | translate }}
              <mat-icon matChipRemove>cancel</mat-icon>
            </mat-chip>
          } @else {
            @for (tool of availableToolConfigs; track tool.labelKey) {
              <mat-chip (click)="onToolChipClick(tool)">{{ tool.labelKey | translate }}</mat-chip>
            }
          }
        </mat-chip-set>
      </div>
    }

    <div
      class="ai-unified-input-shell"
      [class.ai-unified-input-shell-disabled]="hasGeneratedImage || (isAiBusy$ | async)"
    >
      <div
        [matTooltip]="
          hasGeneratedImage || (isAiBusy$ | async)
            ? ((hasGeneratedImage
                ? 'SETUP_GUIDE.AI_CHAT.DISABLED_HINT_IMAGE_GENERATED'
                : 'SETUP_GUIDE.AI_CHAT.DISABLED_HINT_BUSY'
              ) | translate)
            : ''
        "
        matTooltipShowDelay="0"
      >
        <textarea
          #aiPromptInput
          class="ai-prompt-input"
          [placeholder]="
            aiMode === SetupGuideAiModeEnum.Ask
              ? ('SETUP_GUIDE.AI_CHAT.ASK_PROMPT_PLACEHOLDER' | translate)
              : (aiPromptPlaceholder | translate)
          "
          [formControl]="aiPromptControl"
          [attr.disabled]="hasGeneratedImage || (isAiBusy$ | async) ? true : null"
          [rows]="aiMode === SetupGuideAiModeEnum.Ask ? 2 : 3"
        ></textarea>
      </div>

      <div class="ai-input-actions-row">
        <div
          class="ai-mode-field"
          [matTooltip]="
            aiChatMessages.length > 0 ? ('SETUP_GUIDE.AI_CHAT.DISABLED_HINT_ACTIVE_CONVERSATION' | translate) : ''
          "
          matTooltipShowDelay="0"
        >
          <mat-select
            class="ai-mode-select"
            [(value)]="aiMode"
            [disabled]="hasGeneratedImage || (isAiBusy$ | async)"
            panelClass="ai-mode-select-panel"
          >
            <mat-select-trigger>
              <div class="ai-mode-trigger-content">
                <mat-icon class="ai-mode-trigger-icon">{{
                  aiMode === SetupGuideAiModeEnum.Ask ? 'help_outline' : 'auto_fix_high'
                }}</mat-icon>
                <span>{{
                  (aiMode === SetupGuideAiModeEnum.Ask
                    ? 'SETUP_GUIDE.AI_CHAT.MODE_ASK'
                    : 'SETUP_GUIDE.AI_CHAT.MODE_ACT'
                  ) | translate
                }}</span>
              </div>
            </mat-select-trigger>
            @for (mode of aiModeOptions; track mode) {
              <mat-option [value]="mode" [disabled]="isAiModeDisabled(mode)">
                <div class="ai-mode-option-content">
                  <mat-icon class="ai-mode-option-icon">{{
                    mode === SetupGuideAiModeEnum.Ask ? 'help_outline' : 'auto_fix_high'
                  }}</mat-icon>
                  <div class="ai-mode-option-text">
                    <div class="ai-mode-option-title">
                      {{
                        (mode === SetupGuideAiModeEnum.Ask
                          ? 'SETUP_GUIDE.AI_CHAT.MODE_ASK'
                          : 'SETUP_GUIDE.AI_CHAT.MODE_ACT'
                        ) | translate
                      }}
                    </div>
                    <div class="ai-mode-option-hint">
                      {{
                        (isAiModeDisabled(mode) && aiChatMessages.length > 0
                          ? 'SETUP_GUIDE.AI_CHAT.DISABLED_HINT_ACTIVE_CONVERSATION'
                          : mode === SetupGuideAiModeEnum.Ask
                            ? 'SETUP_GUIDE.AI_CHAT.MODE_ASK_HINT'
                            : isAiModeDisabled(mode)
                              ? 'SETUP_GUIDE.AI_CHAT.MODE_ACT_DISABLED_HINT'
                              : 'SETUP_GUIDE.AI_CHAT.MODE_ACT_HINT'
                        ) | translate
                      }}
                    </div>
                  </div>
                </div>
              </mat-option>
            }
          </mat-select>
        </div>

        <button
          #aiGenerateButton
          mat-flat-button
          color="primary"
          class="ai-generate-button"
          [disabled]="hasGeneratedImage || (isAiBusy$ | async)"
          (click)="onGenerateWithAI(autoFillFormType)"
        >
          <gb-button-loader [loaded$]="aiMode === SetupGuideAiModeEnum.Ask ? galabidHelpLoaded$ : autoFillFormLoaded$">
            <span>{{ aiMode === SetupGuideAiModeEnum.Ask ? '' : '✨ ' }}</span>
            <span
              class="ai-generate-label-full"
              [translate]="
                aiMode === SetupGuideAiModeEnum.Ask ? 'SETUP_GUIDE.AI_CHAT.ASK_BUTTON' : 'SETUP_GUIDE.GENERATE_WITH_AI'
              "
            ></span>
            <span
              class="ai-generate-label-short"
              [translate]="
                aiMode === SetupGuideAiModeEnum.Ask
                  ? 'SETUP_GUIDE.AI_CHAT.ASK_BUTTON_SHORT'
                  : 'SETUP_GUIDE.AI_CHAT.GENERATE'
              "
            ></span>
          </gb-button-loader>
        </button>
      </div>
    </div>
  </div>
</div>
